import { SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormInput } from '../input/FormInput'
import { fetchDevicePolicies, fetchDevicePolicy, postUpdateDevicePolicyName } from '../../reducers/settings'
import { useAppDispatch } from '../../reducers/store'
import { DEFAULT_TOAST_CONFIG } from '../toast/consts'
import toast from 'react-hot-toast'
import { CreatePolicyInput, DevicePolicy } from '../../types/SettingsType'
import { IconLoader2 } from '@tabler/icons-react'
import { fetchAllTravelPolicies } from '../../reducers/travel'
import { useEffect, useState } from 'react'

interface IRenamePolicyModalProps {
  setShowRenamePolicyModal: (show: boolean) => void
  policyToAction: DevicePolicy | undefined
  setPolicyToAction: (policy: DevicePolicy | undefined) => void
}

const renamePolicySchema = yup.object().shape({
  name: yup.string().required('Policy name is required'),
  description: yup.string().optional(),
})

function RenamePolicyModal(props: IRenamePolicyModalProps) {
  const di = useAppDispatch()
  const { setShowRenamePolicyModal, policyToAction, setPolicyToAction } = props
  const [policyName, setPolicyName] = useState('')

  const {
    register,
    handleSubmit,
    formState: { isValid },
    reset,
    setValue,
  } = useForm<CreatePolicyInput>({
    resolver: yupResolver(renamePolicySchema),
    mode: 'onChange',
    defaultValues: { name: '', description: '' },
  })

  useEffect(() => {
    if (policyToAction) {
      setPolicyName(policyToAction?.name || '')
      setValue('name', policyToAction?.name || '', { shouldValidate: true })
      setValue('description', policyToAction?.description || '', { shouldValidate: true })
    }
  }, [policyToAction])

  const onSubmit: SubmitHandler<CreatePolicyInput> = async (inputs) => {
    const payload = {
      policyId: policyToAction!.policyId!,
      name: inputs.name,
      description: inputs.description ? inputs.description : undefined,
    }
    const updated = await di(postUpdateDevicePolicyName(payload))

    if (updated) {
      toast.success('Policy updated successfully', DEFAULT_TOAST_CONFIG)
      reset()
      di(fetchDevicePolicy(policyToAction!.policyId!))
      setPolicyToAction(undefined)
      di(fetchDevicePolicies())
      di(fetchAllTravelPolicies())
      setShowRenamePolicyModal(false)
    } else {
      toast.error('Failed to create policy', DEFAULT_TOAST_CONFIG)
    }
  }

  return (
    <div
      className="bg-black bg-opacity-50 absolute top-0 left-0 w-full h-full z-50"
      onClick={() => setShowRenamePolicyModal(false)}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-modalBackground rounded-lg p-6 max-h-[50%] overflow-y-auto border dark:border-gray-600 min-w-[400px] min-h-[224px]"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 className="text-xl font-bold mb-4">Rename Policy</h2>
          {policyToAction !== undefined && (
            <>
              <label className="text-textTertiary text-sm w-full">Policy Name</label>
              <FormInput
                value={policyName}
                type="text"
                {...register('name', { required: true })}
                placeholder="Enter policy name"
                maxLength={60}
              />
              <div className="mt-2">
                <label className="text-textTertiary text-sm w-full">Description (optional)</label>
                <textarea
                  {...register('description')}
                  placeholder="Enter description"
                  maxLength={240}
                  rows={4}
                  className="w-full rounded border border-mainBorder p-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
              <div className="flex justify-end mt-6">
                <button
                  className="px-4 py-2 mr-2 bg-gray-300 rounded-lg text-neutral-12"
                  onClick={() => setShowRenamePolicyModal(false)}
                  type="button"
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 bg-mainPurple text-white rounded-lg ${!isValid ? 'disabled opacity-50 cursor-not-allowed' : 'hover:bg-tertiaryPurple active:active:bg-tertiaryPurple'}`}
                  type="submit"
                  disabled={!isValid}
                >
                  Save
                </button>
              </div>
            </>
          )}
          {policyToAction === undefined && (
            <div className="flex items-center justify-center">
              <IconLoader2 size={64} />
            </div>
          )}
        </div>
      </form>
    </div>
  )
}

export default RenamePolicyModal
